import React from 'react';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import { DateTime } from 'luxon';
import uniqBy from 'lodash.uniqby';

import {
  FREEMIUM_EVENT_STEPS,
  freemiumEventCreateWizardStrings,
} from './constants';
import FreemiumEventCreateDetailsStep from './components/FreemiumEventCreateDetailsStep';
import FreemiumEventCreateReviewStep from './components/FreemiumEventCreateReviewStep';
import {
  ALBUM_SLIDESHOW_MODE,
  ALBUM_SLIDESHOW_STATUS,
  ALBUM_STATUS,
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
  ALBUM_UPLOAD_PERMISSIONS,
} from '../../../../../constants/albums';
import {
  ALBUM_FOLDER_JOIN_TYPE,
  EVENT_SETTING_ANNIVERSARY,
} from '../../../../../constants/album-folder';
import {
  CONTRIBUTOR_UPLOAD_ACCESS,
  CONTRIBUTOR_VIEW_ACCESS,
  PERMISSION_NONE,
} from '../../../../../constants/contributors';
import { getWatermarkConfigInput } from '../../Album/AlbumSettings/AlbumBrandFilterSettings/helpers';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../constants/event-items';
import { defaultWeddingAlbums } from '../WeddingEventCreateWizard/helpers';
import { generateMachineJoinCode } from '../../../../../util/join-code';
import FreemiumEventCreateRegistrationStep from './components/FreemiumEventCreateRegistationStep';
import { isValidPhone } from '../../../../../util/phone';
import { isValidEmail } from '../../../../../util/email';
import FreemiumEventCreatePhoneVerificationStep from './components/FreemiumEventCreatePhoneVerificationStep';
import FreemiumEventCreateEmailPinVerificationStep from './components/FreemiumEventCreateEmailPinVerificationStep';
import FreemiumEventCreateSKUSelectionStep from './components/FreemiumEventCreateSKUSelectionStep';
import FreemiumEventCreateWaldoficationStep from './components/FreemiumEventCreateWaldoficationStep';
import { entityRequiresVGBContext } from '../../../../../util/album';
import FreemiumEventLandingPageStep from './components/FreemiumEventLandingPageStep';
import FreemiumEventCreateUpgradeStep from './components/FreemiumEventCreateUpgradeStep';
import { FREEMIUM_EVENT_TUTORIAL_STEPS } from '../FreemiumEventTutorialWizard/constants';
import FreemiumEventCreatePermissionsStep from './components/FreemiumEventCreatePermissionsStep';
import FreemiumEventCreateSubscriptionUpsellStep from './components/FreemiumEventCreateSubscriptionUpsellStep';
import FreemiumEventCreateUpgradeBundleStep from './components/FreemiumEventCreateUpgradeBundleStep';
import { FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS } from '../../../../shutterbug/views/SignupAlbumCreate/helpers';

export const shouldIncludePermissionsStep = stepProps => {
  if (
    stepProps.standaloneEventAddonType === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK
  ) {
    return true;
  }

  if (
    stepProps.standaloneEventAddonType === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO &&
    ![ALBUM_SUBTYPE.WEDDINGS, ALBUM_SUBTYPE.MEMORIALS].includes(
      stepProps.eventType,
    )
  ) {
    return true;
  }

  return false;
};

export const canCreateFreeEvent = account => {
  // Unauthed situation.
  if (!account) {
    return true;
  }

  if (account.search_subscriptions?.records?.[0]) {
    return true;
  }

  return account.can_create_free_event_album_folder;
};

export const getEventCreateSteps = ({ stepProps = {}, setStepProps }) => {
  const {
    iOSEventAddonItemsBundle,
    eventAddonItems = [],
    selectedEventAddonItemIds = [],
    currentSelectedEventAddonItemIds = [],
    defaultSelectedEventAddonItemIds = [],
    eventAddonItemTypesUpsell = [],
    onEventAddonItemRemove,
    setSelectedEventAddonItemIds,
    includePhotosAlbumDefault,
    isIOSWebView,
    shouldUseIOSBundles,
    waldofiedStepVariant,
  } = stepProps ?? {};
  const waldofiedEventAddonItem = eventAddonItems.find(
    eventAddonItem =>
      eventAddonItem.types.length === 1 &&
      eventAddonItem.types.includes(EVENT_ADDON_ITEM_TYPE.WALDOFIED),
  );
  const bundledEventAddonItem = shouldUseIOSBundles
    ? iOSEventAddonItemsBundle
    : eventAddonItems.find(
        eventAddonItem =>
          eventAddonItem.types.length > 1 &&
          eventAddonItem.types.includes(EVENT_ADDON_ITEM_TYPE.WALDOFIED) &&
          eventAddonItem.types.includes(EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK) &&
          eventAddonItem.types.includes(EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO),
      );

  const waldofiedSelected =
    currentSelectedEventAddonItemIds.includes(waldofiedEventAddonItem?.id) ||
    currentSelectedEventAddonItemIds.includes(bundledEventAddonItem?.id);

  const shouldUseBundleUpgrade =
    (!isIOSWebView || shouldUseIOSBundles) &&
    includePhotosAlbumDefault &&
    (currentSelectedEventAddonItemIds.includes(bundledEventAddonItem?.id) ||
      currentSelectedEventAddonItemIds.includes(waldofiedEventAddonItem?.id));

  const isAutoPurchasingBundle =
    (!isIOSWebView || shouldUseIOSBundles) &&
    includePhotosAlbumDefault &&
    defaultSelectedEventAddonItemIds?.includes(bundledEventAddonItem?.id);

  const shouldSkipSubscriptionUpsellForWaldofiedPurchase =
    !stepProps.isEveryDayShare &&
    (stepProps.freeEventCreationEnabled ||
      (waldofiedSelected &&
        stepProps.waldofiedStepCompleted &&
        !stepProps.subscriptionUpsellCompleted));

  return {
    [FREEMIUM_EVENT_STEPS.LANDING_PAGE]: {
      label: 'Welcome to Waldo',
      key: FREEMIUM_EVENT_STEPS.LANDING_PAGE,
      layoutSize: 'xl',
      hiddenFooter: true,
      skip: true,
      component: props => {
        return (
          <>
            <FreemiumEventLandingPageStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
    },
    [FREEMIUM_EVENT_STEPS.DETAILS]: {
      label: 'General',
      key: FREEMIUM_EVENT_STEPS.DETAILS,
      title: stepProps.isEveryDayShare
        ? "Let's share some photos & videos!"
        : freemiumEventCreateWizardStrings[stepProps.eventType]
            ?.detailsStepBubble,
      layoutSize: 'lg',
      skip: false,
      disableBack: true,
      component: props => (
        <>
          <FreemiumEventCreateDetailsStep
            stepProps={stepProps}
            setStepProps={setStepProps}
            {...props}
          />
        </>
      ),
      validate: async values => {
        const errors = {};

        if (stepProps.eventType === ALBUM_SUBTYPE.WEDDINGS) {
          // This is setup on top in order to keep the error focus order.
          if (!values.bride_name) {
            errors.bride_name = 'Please provide Bride / Groom’s name';
          }

          if (!values.groom_name) {
            errors.groom_name = 'Please provide Bride / Groom’s name';
          }
        }

        if (!values.name) {
          errors.name = 'Please provide a name for your event';
        }

        if (values.name && values.name.length > 120) {
          errors.name = 'Event name cannot be longer than 120 characters.';
        }

        switch (stepProps.eventType) {
          case ALBUM_SUBTYPE.BIRTHDAYS: {
            if (!values.first_name) {
              errors.first_name = 'Please provide a first name';
            }

            if (!values.last_name) {
              errors.last_name = 'Please provide a last name';
            }

            break;
          }
          case ALBUM_SUBTYPE.ANNIVERSARIES: {
            if (!values.anniversary_type) {
              errors.anniversary_type =
                'Please specify the type of anniversary';
            }

            if (values.anniversary_type === EVENT_SETTING_ANNIVERSARY.WEDDING) {
              if (!values.groom_name) {
                errors.groom_name = 'Please provide Bride / Groom’s name';
              }

              if (!values.bride_name) {
                errors.bride_name = 'Please provide Bride / Groom’s name';
              }
            }
            if (values.anniversary_type === EVENT_SETTING_ANNIVERSARY.WORK) {
              if (!values.first_name) {
                errors.first_name = 'Please provide a first name';
              }

              if (!values.last_name) {
                errors.last_name = 'Please provide a last name';
              }

              if (!values.work_start_year) {
                errors.work_start_year = 'Please provide a start year';
              }
            }

            break;
          }
          case ALBUM_SUBTYPE.GRADUATIONS: {
            if (!values.first_name) {
              errors.first_name = 'Please provide a first name';
            }

            if (!values.last_name) {
              errors.last_name = 'Please provide a last name';
            }

            break;
          }

          case ALBUM_SUBTYPE.MEMORIALS: {
            if (!values.first_name) {
              errors.first_name =
                'Please provide a first name for the deceased';
            }

            if (!values.last_name) {
              errors.last_name = 'Please provide a last name for the deceased';
            }

            if (
              values.date_of_birth &&
              values.date_of_passing &&
              values.date_of_birth > values.date_of_passing
            ) {
              errors.date_of_birth =
                'Date of birth cannot be older than date of passing';
            }

            break;
          }
          case ALBUM_SUBTYPE.FUNDRAISERS: {
            if (!values.charity_name) {
              errors.charity_name = 'Please provide a charity name';
            }

            break;
          }
          case ALBUM_SUBTYPE.VACATIONS: {
            if (!values.start_date && values.end_date) {
              errors.start_date = 'Please provide a start date';
            }

            if (
              values.end_date &&
              values.start_date &&
              values.start_date > values.end_date
            ) {
              errors.start_date =
                'Start date cannot be older than the end date';
            }

            if (values.start_date && !values.end_date) {
              errors.end_date = 'Please provide an end date';
            }

            break;
          }
          case ALBUM_SUBTYPE.WEDDINGS: {
            // Bride and groom name errors are evaluted
            // before event.name so that the new order is respected.

            if (stepProps.requireAccountEmail) {
              if (!values.email_address) {
                errors.email_address =
                  'Please provide an email address for your account';
              }

              const emailExists = await stepProps.emailExists(
                values.email_address,
              );

              if (emailExists) {
                errors.email_address =
                  'The email address you selected already exists, please provide another one.';
              }
            }

            if (!values.date) {
              errors.date = 'Please provide a wedding date';
            }

            break;
          }
          default: {
            break;
          }
        }

        return errors;
      },
    },
    [FREEMIUM_EVENT_STEPS.SKU_SELECTION]: {
      label: 'Select your plan',
      key: FREEMIUM_EVENT_STEPS.SKU_SELECTION,
      title: 'Select all the ways you’d like to celebrate your event',
      layoutSize: 'lg',
      skip: !stepProps.requiresSKUSelection,
      component: props => {
        return (
          <>
            <FreemiumEventCreateSKUSelectionStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
      validate: () => {
        const errors = {};

        if (
          !stepProps.includePhotosAlbum &&
          stepProps.currentSelectedEventAddonItemIds.length === 0
        ) {
          errors.sku_selection =
            'Please select at least one way to celebrate your event';
        }

        return errors;
      },
    },
    [FREEMIUM_EVENT_STEPS.WALDOFICATION]: {
      label: 'Waldofication',
      key: FREEMIUM_EVENT_STEPS.WALDOFICATION,
      title: (
        <>
          Go <span className="text-gold">Gold</span> and gather more memories on
          your special day!
        </>
      ),
      layoutSize: 'lg',
      skip:
        isAutoPurchasingBundle ||
        stepProps.isEveryDayShare ||
        (!stepProps.includePhotosAlbumDefault &&
          !stepProps.eventAddonItemTypesUpsell.includes(
            EVENT_ADDON_ITEM_TYPE.WALDOFIED,
          ) &&
          (!stepProps.requiresSKUSelection ||
            !stepProps.includePhotosAlbum ||
            !stepProps.allowFreeAlbums)),
      component: props => {
        return (
          <>
            <FreemiumEventCreateWaldoficationStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
      hasMissingFields: ({ stepProps: latestStepProps }) =>
        !latestStepProps.waldofiedStepCompleted,
      hiddenFooter:
        waldofiedStepVariant ===
        FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS.VARIANT_B,
    },
    [FREEMIUM_EVENT_STEPS.SUBSCRIPTION_UPSELL]: {
      label: 'Subscription Upsell',
      key: FREEMIUM_EVENT_STEPS.SUBSCRIPTION_UPSELL,
      title: (
        <>
          {!stepProps?.isEveryDayShare && (
            <>
              {stepProps?.freeEventCreationEnabled
                ? 'How do you want to gather and share?'
                : 'You have exceeded your free event limit. Please select a plan below!'}
            </>
          )}
          {stepProps?.isEveryDayShare && (
            <>
              {stepProps?.freeEventCreationEnabled
                ? `Try Plus${
                    stepProps?.freeTrialAvailable ? ' for free ' : ' '
                  }and enjoy AI-powered photo sharing, up to 200GB of storage, and more!`
                : "Hey there, you're out of free events but not to worry! You can upgrade to Plus for unlimited events!"}
            </>
          )}
        </>
      ),
      layoutSize: 'lg',
      skip:
        isAutoPurchasingBundle ||
        !stepProps.includePhotosAlbumDefault ||
        stepProps.hasActiveSubscription ||
        shouldSkipSubscriptionUpsellForWaldofiedPurchase ||
        (!stepProps.searchSubscriptionItem &&
          !stepProps.eventAddonItemTypesUpsell.includes(
            EVENT_ADDON_ITEM_TYPE.WALDOFIED,
          ) &&
          (!stepProps.includePhotosAlbum || !stepProps.allowFreeAlbums)),
      skippable: ({ stepProps: latestStepProps }) =>
        !!latestStepProps?.freeEventCreationEnabled,
      onSkip: () => {
        if (waldofiedEventAddonItem) {
          onEventAddonItemRemove(waldofiedEventAddonItem.id);
        }

        setStepProps({
          ...stepProps,
          subscriptionUpsellCompleted: true,
          searchSubscriptionItem: undefined,
        });
      },
      component: props => {
        return (
          <>
            <FreemiumEventCreateSubscriptionUpsellStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
      disableActions: !!stepProps.isEveryDayShare,
      hasMissingFields: ({ stepProps: latestStepProps }) =>
        !latestStepProps.freeEventCreationEnabled &&
        latestStepProps.currentSelectedEventAddonItemIds.length === 0 &&
        !latestStepProps.searchSubscriptionItem,
    },

    [FREEMIUM_EVENT_STEPS.UPGRADE]: {
      label: 'Upgrade your event',
      key: FREEMIUM_EVENT_STEPS.UPGRADE,
      layoutSize: 'lg',
      skip:
        isAutoPurchasingBundle ||
        shouldUseBundleUpgrade ||
        stepProps.requiresSKUSelection ||
        stepProps.isEveryDayShare,
      component: props => {
        return (
          <>
            <FreemiumEventCreateUpgradeStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
      skippable: () => true,
      onSkip: () => {
        //  Since not all add-ons are shown on the upgrade step, use the same
        //  filtering to retrieve the appropriate add-ons.
        const upgradeStepAddonItems = eventAddonItems.filter(eventAddonItem => {
          if (
            includePhotosAlbumDefault &&
            eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED
          ) {
            return false;
          }
          return (
            !selectedEventAddonItemIds.includes(eventAddonItem.id) &&
            !eventAddonItemTypesUpsell.includes(eventAddonItem.types[0])
          );
        });

        onEventAddonItemRemove(upgradeStepAddonItems.map(({ id }) => id));
      },
      hasMissingFields: ({ stepProps: latestStepProps }) =>
        !latestStepProps.upgradeStepCompleted,
    },
    [FREEMIUM_EVENT_STEPS.UPGRADE_BUNDLE]: {
      label: 'Upgrade your event',
      key: FREEMIUM_EVENT_STEPS.UPGRADE_BUNDLE,
      layoutSize: 'lg',
      skip:
        isAutoPurchasingBundle ||
        !shouldUseBundleUpgrade ||
        stepProps.requiresSKUSelection ||
        stepProps.isEveryDayShare,
      component: props => {
        return (
          <>
            <FreemiumEventCreateUpgradeBundleStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
      skippable: () => true,
      onSkip: () => {
        //  Since the bundle includes all item, skipping it defaults back to
        //  a standalone Gold item.
        setSelectedEventAddonItemIds([waldofiedEventAddonItem.id]);
      },
      hasMissingFields: ({ stepProps: latestStepProps }) =>
        !latestStepProps.upgradeStepCompleted,
    },
    [FREEMIUM_EVENT_TUTORIAL_STEPS.PERMISSIONS]: {
      label: 'Permissions',
      key: FREEMIUM_EVENT_TUTORIAL_STEPS.PERMISSIONS,
      layoutSize: 'lg',
      skip: !shouldIncludePermissionsStep(stepProps),
      component: props => {
        return (
          <>
            <FreemiumEventCreatePermissionsStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
    },
    [FREEMIUM_EVENT_STEPS.REVIEW]: {
      label: 'Review',
      key: FREEMIUM_EVENT_STEPS.REVIEW,
      title: (
        <>
          Please review your settings and click <b>create</b> below!
        </>
      ),
      layoutSize: 'lg',
      skip: stepProps.isEveryDayShare,
      component: props => {
        return (
          <FreemiumEventCreateReviewStep
            stepProps={stepProps}
            setStepProps={setStepProps}
            {...props}
          />
        );
      },
      validate: () => {},
    },
    [FREEMIUM_EVENT_STEPS.REGISTRATION]: {
      label: 'Register',
      key: FREEMIUM_EVENT_STEPS.REGISTRATION,
      title: `Almost there - create an account and you're good to go!`,
      layoutSize: 'lg',
      skip: stepProps?.authenticated,
      component: props => {
        return (
          <FreemiumEventCreateRegistrationStep
            stepProps={stepProps}
            setStepProps={setStepProps}
            {...props}
          />
        );
      },
      validate: async values => {
        const { emailExists } = stepProps;
        const errors = {};
        if (!values.account_first_name) {
          errors.account_first_name =
            'Please provide a first name for your account';
        }

        if (!values.account_last_name) {
          errors.account_last_name =
            'Please provide a last name for your account';
        }
        if (
          !values.account_phone_number ||
          !isValidPhone(values.account_phone_number)
        ) {
          errors.account_phone_number =
            'Please provide a valid phone number for your account';
        }
        if (
          !values.account_email_address ||
          !isValidEmail(values.account_email_address)
        ) {
          errors.account_email_address =
            'Please provide a valid email address for your account';
        }

        if (values.account_email_address) {
          const isEmailExists = await emailExists(values.account_email_address);

          if (isEmailExists) {
            errors.account_email_address =
              'The provided email address already exists. Did you meant to sign in?';
            errors.force_login = true;
          }
        }

        return errors;
      },
    },
    [FREEMIUM_EVENT_STEPS.PHONE_VERIFICATION]: {
      label: 'Verify',
      key: FREEMIUM_EVENT_STEPS.PHONE_VERIFICATION,
      title: `Verify the PIN code sent to your phone!`,
      layoutSize: 'lg',
      skip: stepProps?.authenticated,
      component: props => {
        return (
          <FreemiumEventCreatePhoneVerificationStep
            stepProps={stepProps}
            setStepProps={setStepProps}
            {...props}
          />
        );
      },
      validate: values => {
        const errors = {};

        if (!values.account_code) {
          errors.account_code = 'Please enter the provided PIN code';
        }

        return errors;
      },
    },
    [FREEMIUM_EVENT_STEPS.EMAIL_PIN_VERIFICATION]: {
      label: 'Verify',
      key: FREEMIUM_EVENT_STEPS.EMAIL_PIN_VERIFICATION,
      title: `Verify the PIN code sent to your email address!`,
      layoutSize: 'lg',
      skip: stepProps?.authenticated,
      component: props => {
        return (
          <FreemiumEventCreateEmailPinVerificationStep
            stepProps={stepProps}
            setStepProps={setStepProps}
            {...props}
          />
        );
      },
      validate: values => {
        const errors = {};

        if (!values.account_code) {
          errors.account_code = 'Please enter the provided PIN code';
        }

        return errors;
      },
    },
  };
};

/**
 * Determines if the user is missing any required fields
 *
 * @param {ALBUM_SUBTYPE} eventType the event type
 * @param {FREEMIUM_EVENT_STEPS} step the current step
 * @param {Object} values the current set of values
 * @param {Object} stepProps the current set of values
 *
 * @returns {Boolean}
 */
export const hasMissingFields = (eventType, step, values, stepProps) => {
  switch (step) {
    case FREEMIUM_EVENT_STEPS.DETAILS: {
      switch (eventType) {
        case ALBUM_SUBTYPE.BIRTHDAYS: {
          return (
            !values.name ||
            !values.date_of_birth ||
            !values.relationship ||
            !values.first_name ||
            !values.last_name
          );
        }
        case ALBUM_SUBTYPE.ANNIVERSARIES: {
          return (
            !values.name ||
            !values.anniversary_type ||
            !values.relationship ||
            (values.anniversary_type === EVENT_SETTING_ANNIVERSARY.WORK &&
              (!values.first_name ||
                !values.last_name ||
                !values.work_start_year)) ||
            (values.anniversary_type === EVENT_SETTING_ANNIVERSARY.WEDDING &&
              (!values.groom_name ||
                !values.bride_name ||
                !values.wedding_year))
          );
        }
        case ALBUM_SUBTYPE.GRADUATIONS: {
          return (
            !values.name ||
            !values.relationship ||
            !values.first_name ||
            !values.last_name ||
            !values.graduation_date
          );
        }
        case ALBUM_SUBTYPE.MEMORIALS: {
          return (
            !values.name ||
            !values.relationship ||
            !values.first_name ||
            !values.last_name
          );
        }
        case ALBUM_SUBTYPE.FUNDRAISERS: {
          return !values.name || !values.charity_name;
        }
        case ALBUM_SUBTYPE.VACATIONS: {
          return !values.name;
        }
        default: {
          return !values.name;
        }
      }
    }
    case FREEMIUM_EVENT_STEPS.SKU_SELECTION: {
      return (
        !stepProps.includePhotosAlbum &&
        stepProps.currentSelectedEventAddonItemIds.length === 0
      );
    }
    case FREEMIUM_EVENT_STEPS.JOIN_CODE: {
      if (values.join_code && !values.join_code_confirm) {
        return true;
      }

      if (values.public_join_code_verification_enabled && !values.join_code) {
        return true;
      }

      return false;
    }
    case FREEMIUM_EVENT_STEPS.SOCIAL: {
      if (
        values.watermark_config?.asset_image?.id &&
        !values.watermark_config?.placement
      ) {
        return true;
      }

      return false;
    }
    case FREEMIUM_EVENT_STEPS.REGISTRATION: {
      return (
        !values.account_first_name ||
        !values.account_last_name ||
        !values.account_phone_number ||
        !values.account_email_address
      );
    }
    case FREEMIUM_EVENT_STEPS.PHONE_VERIFICATION: {
      return !values.account_code;
    }
    case FREEMIUM_EVENT_STEPS.EMAIL_PIN_VERIFICATION: {
      return !values.account_code;
    }
    case FREEMIUM_EVENT_STEPS.WALDOFICATION: {
      return !stepProps.waldofiedStepCompleted;
    }
    case FREEMIUM_EVENT_STEPS.UPGRADE: {
      return stepProps.currentSelectedEventAddonItemIds.length === 0;
    }
    case FREEMIUM_EVENT_STEPS.UPGRADE_BUNDLE: {
      return stepProps.currentSelectedEventAddonItemIds.length === 0;
    }
    default: {
      return false;
    }
  }
};

/**
 * Prepares the input based off of user values to update/create an album.
 *
 * @param {Object} opts
 * @param {Object} opts.values List of user configurated values
 * @returns {{end_date: Date | *, watermark: {asset_image_id: string, placement: (*|null)}, enable_external_upload: boolean, is_web_gallery: boolean, privacy, invite_only_access, auto_contributor_settings: ({view_access: (CONTRIBUTOR_VIEW_ACCESS|CONTRIBUTOR_VIEW_ACCESS), upload_access: (CONTRIBUTOR_UPLOAD_ACCESS|CONTRIBUTOR_UPLOAD_ACCESS), video_upload_access: (CONTRIBUTOR_VIDEO_UPLOAD_ACCESS|CONTRIBUTOR_VIDEO_UPLOAD_ACCESS)}|{view_access: (CONTRIBUTOR_VIEW_ACCESS|CONTRIBUTOR_VIEW_ACCESS), upload_access: string, video_upload_access: string}), external_uploads_require_review, password, hash_code, member_uploads_require_review, is_private_web_gallery: boolean, web_gallery_print_purchases_enabled: boolean, name, start_date: Date | *}}
 */
const getAlbumConfig = ({ values }) => {
  return {
    name: values.name,
    privacy: values.privacy,
    start_date: startOfDay(values.date),
    end_date: endOfDay(values.date),
    password: values.password,
    invite_only_access: values.invite_only_access,
    is_web_gallery: true,
    is_private_web_gallery: false,
    web_gallery_print_purchases_enabled: true,
    watermark: getWatermarkConfigInput(values.watermark_config),
    hash_code: values.hash_code,
  };
};

/**
 * Prepares the input based off of user values to update/create an albumFolder.
 *
 * @param {Object} opts
 * @param {Object} opts.album An album object (generally individual album) containing the albumFolderId
 * @param {Object} opts.values List of user configurated values
 * @returns {{end_date: Date | *, join_type: (ALBUM_FOLDER_JOIN_TYPE|ALBUM_FOLDER_JOIN_TYPE), album_folder_id, enable_external_upload: boolean, search_subscription_credits_joins_enabled: boolean, event_settings: {groom_name: (string|*), date_of_passing: (string|null), anniversary_type: (string|null|*), date_of_birth: (string|null), event_date: (string|null), bride_name: (string|*), last_name, couple_email_address: (string|*), relationship: (*|undefined), first_name, charity_name: (string|*)}, auto_contributor_settings: ({view_access: (CONTRIBUTOR_VIEW_ACCESS|CONTRIBUTOR_VIEW_ACCESS), upload_access: (CONTRIBUTOR_UPLOAD_ACCESS|CONTRIBUTOR_UPLOAD_ACCESS), video_upload_access: (CONTRIBUTOR_VIDEO_UPLOAD_ACCESS|CONTRIBUTOR_VIDEO_UPLOAD_ACCESS)}|{view_access: (CONTRIBUTOR_VIEW_ACCESS|CONTRIBUTOR_VIEW_ACCESS), upload_access: string, video_upload_access: string}), display_public_photo_attribution: boolean, external_uploads_require_review, consumer_display_empty_albums: boolean, display_photos_attribution: boolean, member_uploads_require_review, start_date: Date | *}}
 */
const getAlbumFolderConfig = ({ album, values }) => {
  return {
    album_folder_id: album.album_folder.id,
    join_type: ALBUM_FOLDER_JOIN_TYPE.ALL,
    consumer_display_empty_albums: true,
    display_photos_attribution: true,
    display_public_photo_attribution: true,
    search_subscription_credits_joins_enabled: true,
    start_date: startOfDay(values.start_date ? values.start_date : values.date),
    end_date: endOfDay(values.end_date ? values.end_date : values.date),
    enable_external_upload:
      values.permission === ALBUM_UPLOAD_PERMISSIONS.ANYONE,
    auto_contributor_settings:
      values.permission === ALBUM_UPLOAD_PERMISSIONS.ANYONE
        ? {
            upload_access: values.require_review
              ? CONTRIBUTOR_UPLOAD_ACCESS.REVIEW
              : CONTRIBUTOR_UPLOAD_ACCESS.PUBLISH,
            view_access: CONTRIBUTOR_VIEW_ACCESS.ALL,
          }
        : {
            view_access: CONTRIBUTOR_VIEW_ACCESS.ALL,
            upload_access: PERMISSION_NONE,
          },
    external_uploads_require_review: values.require_review,
    member_uploads_require_review: values.require_review,
    event_settings: {
      bride_name: values.bride_name,
      groom_name: values.groom_name,
      couple_email_address: values.couple_email_address,
      relationship: values.relationship ? values.relationship : undefined,
      anniversary_type: values.anniversary_type,
      first_name: values.first_name,
      last_name: values.last_name,
      charity_name: values.charity_name,
      event_date: values.date
        ? DateTime.fromJSDate(values.date).toISODate()
        : null,
      date_of_birth: values.date_of_birth
        ? DateTime.fromJSDate(values.date_of_birth).toISODate()
        : null,
      date_of_passing: values.date_of_passing
        ? DateTime.fromJSDate(values.date_of_passing).toISODate()
        : null,
      graduation_date: values.graduation_date
        ? DateTime.fromJSDate(values.graduation_date).toISODate()
        : null,
      wedding_year:
        !values.wedding_year || Number.isNaN(values.wedding_year)
          ? null
          : values.wedding_year,
      work_start_year:
        !values.work_start_year || Number.isNaN(values.work_start_year)
          ? null
          : values.work_start_year,
    },
    create_giftable_event_addon_types:
      values.create_giftable_event_addon_types ?? [],
  };
};

/**
 * Sets up a new Draft album (or updates an existing one if provided) with the values hte user
 * has specified. To complete the general process of checkout `processAlbumPostCheckout` should be
 * called.
 *
 * @param {Object} opts
 * @param {Object} opts.values Object of values specified by the user.
 * @param {String} opts.albumId If the user backs out from checkout and/or updates values in between, this is the album that was created in  the current session.
 * @param {Function} opts.albumCreate Method to create an album.
 * @param {Function} opts.albumUpdate Method to update an album.
 * @param {Function} opts.albumFolderUpdate Method to update an albumFolder.
 * @param {Object} opts.user Current authed user.
 * @param {String} opts.eventType The event type (albumSubtype).
 * @param {String} opts.isEveryDayShare Wether or not the album was created via Everyday Share
 * @returns {Promise<{album}>}
 */
export const prepareAlbumForCheckout = async ({
  values,
  albumId,
  albumCreate,
  albumUpdate,
  albumFolderUpdate,
  user,
  eventType,
  isEveryDayShare,
}) => {
  let album;

  if (albumId) {
    const {
      data: {
        albumUpdate: { album: updatedAlbum },
      },
    } = await albumUpdate({
      id: albumId,
      ...getAlbumConfig({ values }),
    });

    album = updatedAlbum;
  } else {
    const {
      data: {
        albumCreate: { album: createdAlbum },
      },
    } = await albumCreate({
      creator_id: user?.id,
      album_type: isEveryDayShare ? ALBUM_TYPE.SHUTTERBUG : ALBUM_TYPE.EVENT,
      album_subtype: eventType,
      ...getAlbumConfig({ values }),
      status: ALBUM_STATUS.DRAFT,
    });

    album = createdAlbum;
  }

  await albumFolderUpdate(getAlbumFolderConfig({ album, values }));

  return { album };
};

const processMemoryBookFlow = async ({
  album,
  purchasedEventAddons,
  values,
  albumUpdate,
  includePhotosAlbum,
}) => {
  const memoryBookAlbum = purchasedEventAddons.find(
    ({ event_addon_type: type }) => type === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
  ).album;

  const isOnlyAlbum = !includePhotosAlbum && purchasedEventAddons?.length === 1;

  await albumUpdate({
    id: memoryBookAlbum.id,
    name: isOnlyAlbum ? values.name : undefined,
    joins_enabled: false,
    enable_external_upload: true,
    external_uploads_require_review: values.require_review,
    auto_contributor_settings: {
      view_access: CONTRIBUTOR_VIEW_ACCESS.OWN,
      upload_access: values.require_review
        ? CONTRIBUTOR_UPLOAD_ACCESS.REVIEW
        : CONTRIBUTOR_UPLOAD_ACCESS.PUBLISH,
    },
    slideshow: {
      status: ALBUM_SLIDESHOW_STATUS.DRAFT,
      mode: ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
    },
  });

  if (isOnlyAlbum) {
    return memoryBookAlbum;
  }

  return album;
};

const processGroupVideoFlow = async ({
  album,
  purchasedEventAddons,
  values,
  albumUpdate,
  includePhotosAlbum,
}) => {
  const groupVideoAlbum = purchasedEventAddons.find(
    ({ event_addon_type: type }) => type === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
  ).album;

  const isOnlyAlbum = !includePhotosAlbum && purchasedEventAddons?.length === 1;

  const isVGB = entityRequiresVGBContext(groupVideoAlbum);

  await albumUpdate({
    id: groupVideoAlbum.id,
    name: isOnlyAlbum ? values.name : undefined,
    joins_enabled: false,
    enable_external_upload: true,
    external_uploads_require_review: values.require_review,
    auto_contributor_settings: {
      view_access: CONTRIBUTOR_VIEW_ACCESS.OWN,
      // API Cares only for upload_access regardless of being for photos or videos..
      upload_access: values.require_review
        ? CONTRIBUTOR_UPLOAD_ACCESS.REVIEW
        : CONTRIBUTOR_UPLOAD_ACCESS.PUBLISH,
    },
    slideshow: {
      status: isVGB
        ? ALBUM_SLIDESHOW_STATUS.PUBLISHED
        : ALBUM_SLIDESHOW_STATUS.DRAFT,
      mode: ALBUM_SLIDESHOW_MODE.ALL_VIDEOS,
    },
  });

  if (isOnlyAlbum) {
    return groupVideoAlbum;
  }

  return album;
};

const processWeddingsFlow = async ({
  album,
  values,
  albumUpdate,
  albumCreate,
  albumFolderUpdate,
  purchasedEventAddons,
  user,
}) => {
  const customAlbums = values.albums.filter(
    albumItem => albumItem.selected && !albumItem.is_wedding_day_album,
  );

  const weddingDayAlbum = values.albums.find(
    albumItem => albumItem.is_wedding_day_album,
  );

  const includesWaldofied =
    purchasedEventAddons?.length > 0 &&
    !!purchasedEventAddons.find(({ event_addon_type: type }) =>
      [EVENT_ADDON_ITEM_TYPE.WALDOFIED].includes(type),
    );

  await albumFolderUpdate({
    album_folder_id: album?.album_folder?.id,
    individual_albums: false,
  });

  await albumUpdate({
    id: album.id,
    name: weddingDayAlbum?.name,
    is_wedding_day_album: true,
    slideshow: includesWaldofied
      ? {
          status: ALBUM_SLIDESHOW_STATUS.DRAFT,
          mode: ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
        }
      : undefined,
  });

  if (customAlbums.length > 0) {
    await Promise.all(
      customAlbums.map(async customAlbum => {
        const {
          data: {
            albumCreate: { album: createdAlbum },
          },
        } = await albumCreate({
          name: customAlbum.name,
          parent_album_folder_id: album?.album_folder?.id,
          creator_id: user?.id,
          password: values.password,
          invite_only_access: values.invite_only_access,
          privacy: values.privacy,
          album_type: ALBUM_TYPE.EVENT,
          album_subtype: ALBUM_SUBTYPE.WEDDINGS,
          join_code: generateMachineJoinCode(),
          display_order: customAlbum.display_order,
          enable_external_upload:
            values.permission === ALBUM_UPLOAD_PERMISSIONS.ANYONE,
          external_uploads_require_review: values.require_review,
        });

        return createdAlbum;
      }),
    );
  }
};

/**
 * Completes the checkout process, marking the album as active and, as such, available for
 * the consumer.
 *
 * @param {Object} opts
 * @param {Object} opts.values Object of values specified by the user.
 * @param {String} opts.album A draft album object.
 * @param {Array<Object>} opts.purchasedEventAddons List of purchased event addons from the albumFolder.
 * @param {Function} opts.albumUpdate Method to update an album.
 * @param {Function} opts.albumFolderUpdate Method to update an albumFolder.
 * @param {Object} opts.user Current authed user.
 * @param {String} opts.eventType The event type (albumSubtype).
 * @param {String} opts.includePhotosAlbum Whether the user has decided to include photo albums or not.
 * @returns {Promise<{redirectAlbum, redirectToAlbumFolder: boolean, isPurchased: boolean}>}
 */
export const processAlbumPostCheckout = async ({
  album,
  values,
  purchasedEventAddons,
  eventType,
  user,
  albumCreate,
  albumUpdate,
  albumDelete,
  albumFolderUpdate,
  includePhotosAlbum,
}) => {
  const albumFolderId = album?.album_folder.id;
  let redirectAlbum = album;

  if (values.join_code && purchasedEventAddons?.length > 0) {
    // We can add the join code to the album folder as the user performed checkout:
    await albumFolderUpdate({
      album_folder_id: albumFolderId,
      join_code: values.join_code,
    });
  }

  // If we have purchased a specialty album, we need to update it with the
  // appropriate permissions we have selected for it.
  if (
    purchasedEventAddons?.length > 0 &&
    purchasedEventAddons.find(({ event_addon_type: type }) =>
      [EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK].includes(type),
    )
  ) {
    redirectAlbum = await processMemoryBookFlow({
      album,
      purchasedEventAddons,
      values,
      albumUpdate,
      includePhotosAlbum,
    });
  }

  if (
    purchasedEventAddons?.length > 0 &&
    purchasedEventAddons.find(({ event_addon_type: type }) =>
      [EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO].includes(type),
    )
  ) {
    redirectAlbum = await processGroupVideoFlow({
      album,
      purchasedEventAddons,
      values,
      albumUpdate,
      includePhotosAlbum,
    });
  }

  if (!includePhotosAlbum) {
    await albumDelete({ id: album.id });
    if (purchasedEventAddons?.length === 1) {
      await albumFolderUpdate({
        album_folder_id: albumFolderId,
        individual_albums: true,
      });
    }
  } else {
    await albumUpdate({ id: album.id, status: ALBUM_STATUS.ACTIVE });
  }

  if (eventType === ALBUM_SUBTYPE.WEDDINGS && includePhotosAlbum) {
    await processWeddingsFlow({
      album,
      values,
      purchasedEventAddons,
      user,
      albumCreate,
      albumUpdate,
      albumFolderUpdate,
      includePhotosAlbum,
    });
    // Enable Smart Layouts for Waldofied.
    // This is executed in the wedding process, but we need to execute for non-weddings.
  } else if (
    includePhotosAlbum &&
    purchasedEventAddons?.length > 0 &&
    purchasedEventAddons.find(({ event_addon_type: type }) =>
      [EVENT_ADDON_ITEM_TYPE.WALDOFIED].includes(type),
    )
  ) {
    await albumUpdate({
      id: album.id,
      slideshow: {
        status: ALBUM_SLIDESHOW_STATUS.DRAFT,
        mode: ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
      },
    });
  }

  const redirectToAlbumFolder = (() => {
    if (eventType === ALBUM_SUBTYPE.WEDDINGS) {
      return true;
    }

    return includePhotosAlbum
      ? !!(purchasedEventAddons ?? []).find(
          ({ type }) => type !== EVENT_ADDON_ITEM_TYPE.WALDOFIED,
        )
      : (purchasedEventAddons ?? []).length > 1;
  })();

  return {
    isPurchased: true,
    redirectAlbum,
    redirectToAlbumFolder,
    purchasedEventAddons,
  };
};

export const getCreateWizardAlbums = ({
  defaultAlbums = defaultWeddingAlbums,
  ignoreWeddingDayAlbum = false,
} = {}) => {
  if (ignoreWeddingDayAlbum) {
    return defaultAlbums;
  }

  const existingWeddingDayAlbum = defaultAlbums?.find(
    album => album.id === 'WEDDING_DAY_ALBUM',
  );

  const weddingDayAlbumItem = {
    name: 'Wedding Day',
    id: 'WEDDING_DAY_ALBUM',
    removable: false,
    is_wedding_day_album: true,
    display_order: 1,
    selected: true,
    permanent: true,
    // Ensures we keep in mind user's selection
    ...(existingWeddingDayAlbum || {}),
  };

  return uniqBy([weddingDayAlbumItem, ...defaultAlbums].filter(Boolean), 'id');
};
